import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    ChatMessageData,
    isChatNode,
    isDisplayNode,
    isMobileBrowser,
    isModelNode,
    LogLevel,
    NodeInput,
    richToPlain,
    smoothScrollTo
} from "api-sdk"
import { ModelNodeSteps, RichText, useInputModule, useMenu, useReaderModule } from "api-sdk-react"
import {
    ActionButton,
    createMessages,
    ErrorState,
    HighlightedMedia,
    isContainedInNestedModelPortal,
    MessagesContext,
    NestedModelPortalControls,
    postEventMessage,
    ProgressIndicator,
    ReaderApp,
    ReaderStatus,
    shouldSubmitOnSelect,
    useAccessibilityOptions,
    useNestedModelPortal
} from "reader-common";
import { PartnerMessage } from "./components/chat/partnerMessage";
import { UserMessage } from "./components/chat/userMessage";
import BooleanInput from "./input/booleanInput";
import { DateInput } from "./input/dateInput";
import { FileInput } from "./input/fileInput";
import { InputContainer } from "./input/inputContainer";
import { MultipleChoiceInput } from "./input/multipleChoiceInput";
import { NumberInput } from "./input/numberInput";
import { SingleChoiceInput } from "./input/singleChoiceInput";
import { DynamicChoiceInput } from "./input/dynamicChoiceInput";
import { TextInput } from "./input/textInput";
import { ChatStyleConfig, ReaderConfigWithStyleConfig } from "./types";
import { ChatMessageHeader } from "./components/chat/chatMessageHeader";

import "api-sdk-react/dist/styles.css";
import "reader-common/dist/styles.css";
import "./styles/style.scss"
import { ChatNodeMessages } from "./components/chat/chatNodeMessages"

/* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
const windowAny = (window as any)

/** Chat-design reader  */
export function ChatReader() {

    const readerConfig = (windowAny.__cdc_readerConfig as ReaderConfigWithStyleConfig);

    // Declare state & effects
    const readerState = useReaderModule(() => new ReaderApp({
        apiUrl: windowAny.__cdc_apiUrl || window.location.origin,
        modelId: windowAny.__cdc_modelId as string,
        runId: windowAny.__cdc_runId as string,
        readerConfig: readerConfig,
        logLevel: LogLevel.WARN,
        pollOnRunWaiting: true,
    }))

    const [ animateEditAnswerForNode, setAnimateEditAnswerForNode ] = useState<number | undefined>(undefined)
    /** When true, animate user input to user message at the given history index */
    const [ animateToMessageAtHistoryIdx, setAnimateToMessageAtHistoryIdx ] = useState<number>(-1)
    const [ lastReaderFrameScrollPosition, setLastReaderFrameScrollPosition ] = useState(0)

    // Shortcut variables
    const runState = readerState.run?.state
    const config = readerState.readerModule?.getReaderConfig()
    const node = runState?.node
    const isRunFinished = (node == null && !runState?.waitingState) || (!!node && (isDisplayNode(node) && node.isEndNode))

    const readerContainer = useRef<HTMLDivElement>(undefined)
    const readerFrame = useRef<HTMLDivElement>(undefined)
    const inputContainer = readerFrame?.current?.getElementsByClassName("input-container")[0]

    const [ menu, menuCtrls ] = useMenu()
    const [ accessibilityOptions, accessibilityMenu ] = useAccessibilityOptions(config)

    const [ inputMap, fileMap, setInputMap, renderInputFields ] = useInputModule({
        componentPropSupplier: () => ({
            config: config,
            fileUrlProvider: readerState.getFileUrl,
            menu: menuCtrls
        }),
        onComponentChange: (updated: NodeInput) => !config.confirmSingleChoice
            && shouldSubmitOnSelect(updated, runState.node) && handleSubmit({ type: "INPUT" }),
        autoFocusInput: !accessibilityOptions.isUsingScreenReader && !isMobileBrowser(),
        components: {
            ["TEXT"]: TextInput,
            ["NUMBER"]: NumberInput,
            ["BOOLEAN"]: BooleanInput,
            ["DATE"]: DateInput,
            ["FILE"]: FileInput,
            ["MULTIPLE_CHOICE"]: MultipleChoiceInput,
            ["SINGLE_CHOICE"]: SingleChoiceInput,
            ["DYNAMIC_CHOICE"]: DynamicChoiceInput,
        },
        history: runState?.history,
        proposedInputMap: runState?.proposedInput,
    })

    // Use effect to remove animation flag once it's finished
    useEffect(() => {
        if (!readerState.isLoading) {
            setAnimateEditAnswerForNode(undefined)
        }
    }, [ readerState.isLoading ])

    const animationInfo: AnimationInfo = useMemo(() => {
        return node?.nodeType == "INPUT" ? ({
            type: AnimationType.DEFAULT,
            duration: 800
        }) : ({
            type: AnimationType.PASSIVE,
            duration: 500
        })
    }, [ node ])

    const handleSubmit = useCallback((data: { type: "INPUT" } | { type: "CHAT", message: ChatMessageData }) => {
        const onSubmit = () => data.type === "INPUT"
            ? readerState.readerModule.prepareAndSubmit(inputMap, fileMap)
            : readerState.readerModule.sendChatMessage(data.message)

        if (readerContainer.current) {
            readerContainer.current.style.minHeight = readerContainer.current.scrollHeight + "px"
        }

        if (readerFrame.current) {
            const scrollPos = readerFrame.current.scrollTop
            if (scrollPos != lastReaderFrameScrollPosition) {
                setLastReaderFrameScrollPosition(scrollPos)
            }
        }

        if (animationInfo.type !== AnimationType.NONE) {
            setAnimateToMessageAtHistoryIdx(runState.history.path.length - 1)
            setTimeout(onSubmit, animationInfo.duration)
        } else {
            onSubmit()
        }
    }, [ animationInfo, setAnimateToMessageAtHistoryIdx, readerState,
        inputMap, fileMap, lastReaderFrameScrollPosition, readerFrame, readerContainer,
        runState?.history?.path?.length
    ])

    // Disable animateToMessage on prop change
    useEffect(() => {
        setAnimateToMessageAtHistoryIdx(-1)
    }, [ node ])

    // Enable automatic scrolling
    useEffect(() => {

        const isAnimatingToMessage = (readerState.run?.state.history.path.length - 1) == animateToMessageAtHistoryIdx
        if (readerFrame?.current != undefined && readerContainer?.current != undefined && !isAnimatingToMessage) {

            // Scroll to the latest partner message
            if (lastReaderFrameScrollPosition != undefined) {
                readerFrame.current.scrollTop = lastReaderFrameScrollPosition;
            }

            let firstActivePartnerMessage: HTMLElement = null
            for (const child of Array.from(readerContainer.current.children).reverse()) {
                if (child.className.includes("user-message")) {
                    break;
                } else if (child.className.includes("partner-message")) {
                    firstActivePartnerMessage = child as HTMLElement
                }
            }

            const lastReaderContainerChild: HTMLElement = readerContainer.current.children[readerContainer.current.childElementCount - 1] as HTMLElement

            if (firstActivePartnerMessage && lastReaderContainerChild) {

                // The outer container that scrolls
                const readerFrameElem = readerFrame.current

                // Find the top and bottom positions of the rectangle that should be visible (target)
                const targetTop = firstActivePartnerMessage.offsetTop
                const targetBottom = lastReaderContainerChild.offsetTop + lastReaderContainerChild.clientHeight
                // Find the top and bottom positions of the actual viewport rectangle (frameViewPort)
                const frameViewPortTop = readerFrameElem.scrollTop
                const frameViewPortBottom = frameViewPortTop + readerFrameElem.clientHeight

                let scrollBy = 0

                // Add padding when scrolling so the target viewport doesn't align with the edges of the actual viewport
                const SCROLL_VIEWPORT_PADDING = 10

                if (targetTop < frameViewPortTop) {
                    // If the target viewport is "above" the actual view port, scroll down to show it
                    scrollBy = targetTop - frameViewPortTop + SCROLL_VIEWPORT_PADDING
                } else if (targetBottom > frameViewPortBottom) {
                    /*
                        If the target viewport is "below" the actual view port, scroll up to show it.
                        However, when the target viewport is larger than the actual viewport,
                        scroll to the top of the target view port at most, to avoid ending up in the same case as above
                     */
                    scrollBy = Math.min(
                        targetBottom - frameViewPortBottom,
                        targetTop - frameViewPortTop + SCROLL_VIEWPORT_PADDING
                    )
                }

                if (scrollBy != 0) {
                    const scrollToPosition = frameViewPortTop + scrollBy
                    setTimeout(() => smoothScrollTo(readerFrameElem, scrollToPosition, 300), 300)
                }

            }
        }
    }, [
        readerState.run?.state, readerFrame, inputContainer,
        readerContainer, isRunFinished, lastReaderFrameScrollPosition,
        animateToMessageAtHistoryIdx
    ])

    function renderHistory() {

        let historyPath = runState.history.path
        const isVisibleStep = runState.node != undefined // Whether current step contains a display node

        if (isVisibleStep) {
            historyPath = historyPath.slice(0, historyPath.length - 1) // Remove current from history
        }

        function renderHighlightedMediaCaption(caption: string, animationDelay: string) {
            return (
                <div key={ caption } className="message partner-message">
                    <ChatMessageHeader
                        config={ config }
                        styleConfig={ readerConfig.styleConfig }
                        target="partner"
                    />
                    <div
                        className="message-content animated-entry-partner"
                        style={{ animationDelay: animationDelay }}
                        aria-label={ richToPlain(caption) }
                        aria-live="polite">
                        <RichText text={ caption } aria-hidden />
                    </div>
                </div>
            )
        }

        const pathViews = historyPath.reduce((views, step, idx) => {
            const animateLeave = (shiftBy: 0 | -1) => (animateEditAnswerForNode != null
                && ((idx + shiftBy) >= historyPath.findIndex(stp => stp.node.id === animateEditAnswerForNode)))
            const key = `${idx}/${step.node.id}`
            views.push(
                <PartnerMessage
                    config={ config }
                    fileUrlProvider={ readerState.getFileUrl }
                    styleConfig={ readerConfig.styleConfig }
                    menu={ menuCtrls }
                    node={ step.node }
                    animateLeave={ animateLeave(-1) }
                    key={ key }
                />
            )
            views.push(
                <HighlightedMedia
                    key={ "highlight-" + key }
                    fileUrlProvider={ readerState.getFileUrl }
                    media={ step.node.highlightedMedia || [] }
                    config={ config }
                    renderCaption={ renderHighlightedMediaCaption }
                />
            )
            if (isModelNode(step.node) && step.node.dispatchSteps?.length > 0) {
                views.push(
                    <div className="model-node-steps" key={ "steps-" + key }>
                        <ModelNodeSteps node={ step.node } isFinished />
                    </div>
                )
            } else if (isChatNode(step.node)) {
                views.push(
                    <ChatNodeMessages
                        node={ step.node }
                        config={ config }
                        styleConfig={ readerConfig.styleConfig }
                        menu={ menuCtrls }
                        animateLeave={ animateLeave(0) }
                    />
                )
            } else {
                views.push(
                    <UserMessage
                        key={ "user-" + key }
                        config={ config }
                        styleConfig={ readerConfig.styleConfig }
                        runState={ runState }
                        nodeInputMap={ Object.assign({ }, inputMap, step.inputMap) }
                        fileMap={ fileMap }
                        node={ step.node }
                        onEdit={ () => {
                            if (readerContainer.current) {
                                // Remove minHeight from chat to prevent the window from staying big while the content gets smaller
                                delete readerContainer.current.style.minHeight;
                            }
                            setAnimateEditAnswerForNode(step.node.id)
                            setTimeout(() => readerState.readerModule.go(idx), 400)
                        } }
                        canEdit={ step.isReversible && isVisibleStep && !isRunFinished }
                        animateLeave={ animateLeave(0) }
                    />
                )
            }
            return views
        }, [])

        if (runState.node != undefined) {
            pathViews.push(
                <PartnerMessage
                    className={ "active " + ((animateEditAnswerForNode != undefined) ? "animated-leave-user" : "") }
                    config={ config }
                    fileUrlProvider={ readerState.getFileUrl }
                    styleConfig={ readerConfig.styleConfig }
                    menu={ menuCtrls }
                    node={ runState.node }
                    animateEntry
                    animateLeave={ animateEditAnswerForNode != undefined }
                    autoFocus={ accessibilityOptions.isUsingScreenReader }
                    key={ runState.node.id }
                />
            )
            pathViews.push(
                <HighlightedMedia
                    key={ "highlight-" + runState.node.id }
                    fileUrlProvider={ readerState.getFileUrl }
                    media={ runState.node.highlightedMedia || [] }
                    config={ config }
                    renderCaption={ renderHighlightedMediaCaption }
                    animateEntry
                />
            )
            if (isChatNode(runState.node)) {
                pathViews.push(
                    <ChatNodeMessages
                        node={ runState.node }
                        config={ config }
                        styleConfig={ readerConfig.styleConfig }
                        menu={ menuCtrls }
                        animateLeave={ animateEditAnswerForNode != undefined }
                        animateEntry
                    />
                )
            }
        }


        return pathViews
    }

    function renderInputSection(nestedModelPortalCtrls: NestedModelPortalControls)
    {
        if (!readerState.run?.result) {
            return (
                <InputContainer
                    key="in"

                    inputMap={ inputMap }
                    fileMap={ fileMap }
                    setInputMap={ setInputMap }
                    renderInputFields={ renderInputFields }
                    animateToMessageAtHistoryIdx={ animateToMessageAtHistoryIdx }
                    animationInfo={ animationInfo }

                    run={ readerState.run }
                    node={ runState.node }
                    config={ config }
                    fileUrlProvider={ readerState.getFileUrl }
                    styleConfig={ readerConfig.styleConfig }
                    menu={ menuCtrls }
                    onSubmit={ () => handleSubmit({ type: "INPUT" }) }
                    onSendChatMessage={ (message: ChatMessageData) => handleSubmit({ type: "CHAT", message }) }
                    nestedModelPortalControls={ nestedModelPortalCtrls }

                    isLoading={ readerState.isLoading || !runState.node == undefined }
                    animateLeave={ animateEditAnswerForNode != undefined }
                />
            )
        }
        return null
    }

    let readerContainerElement: React.JSX.Element
    const [ nestedModelPortalCtrls, nestedModelPortal, isNestedModelPortalShown ] = useNestedModelPortal()

    // Render
    if (readerState.runError) {
        readerContainerElement = (
            <ErrorState config={ config } />
        )
    } else if (readerState.readerModule && readerState.run) {
        const config = readerState.readerModule.getReaderConfig()

        const runFinishedIndicator = isRunFinished ? (
            <div className="run-finished">
                <div className="upper"></div>
                <div className="lower"></div>
            </div>
        ) : null

        readerContainerElement = (
            <div className={ "reader-container" } ref={ readerContainer }>
                { !config.hideAccessibilityOptions && accessibilityMenu }
                { renderHistory() }
                { !isRunFinished && renderInputSection(nestedModelPortalCtrls) }
                { runFinishedIndicator }
            </div>
        )
    } else {
        readerContainerElement = (
            <div className="reader-container" ref={ readerContainer }>
                <div className="message partner-message">
                    <div className="message-content center animated-partner-entry">
                        <ProgressIndicator />
                    </div>
                </div>
            </div>
        )
    }

    let chatRootClassName = "chat-root"
    Object.keys(readerConfig.styleConfig).forEach(fieldName => {
        const styleValue = String(readerConfig.styleConfig[fieldName as keyof ChatStyleConfig])?.toLowerCase().replace(/_/g, "-")
        chatRootClassName += " chat-" + fieldName + "-" + styleValue
    })

    const showDismissPortalButton = isContainedInNestedModelPortal() && readerState.run?.result

    return (
        <MessagesContext.Provider value={ createMessages(readerState?.run?.state?.locale) }>
            <div className={ chatRootClassName }>
                <div className="chat">
                    <div className={ "reader-frame" + (isNestedModelPortalShown ? " hidden" : "") } role="main" ref={ readerFrame }>
                        { readerContainerElement }
                    </div>
                    <div className={ "dismiss-portal-container" + (showDismissPortalButton ? " shown" : "") }>
                        <ActionButton
                            className="dismiss-portal-button"
                            text={ config.messages.next }
                            iconName="chevron_right"
                            iconPosition="end"
                            onClick={ () => postEventMessage(ReaderStatus.DISMISS_PORTAL(readerState.run), false) }
                        />
                    </div>
                    { nestedModelPortal }
                    { menu }
                </div>
            </div>
        </MessagesContext.Provider>
    )


}

export interface AnimationInfo {
    type: AnimationType,
    duration: number
}

export enum AnimationType {
    NONE = "none",
    PASSIVE = "passive",
    DEFAULT = "default"
}
